import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useRef } from "react";
import { CustomToastComponent } from "@/components/shared";
import { client } from "@/client";
import { useMutation } from "@apollo/client/react";
import {
  RebalanceCarRatiosMutation,
  RebalanceCarRatiosMutationVariables,
  RebalanceCarRatiosDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
  FetchCarsOnTenderDocument,
} from "@/graphql/__generated__/graphql";

interface RebalanceCarRatiosOptions {
  disableToast?: boolean;
}

/**
 * Custom hook for rebalancing car service ratios on a tender.
 *
 * @param {RebalanceCarRatiosOptions} options - Options for the hook.
 * @returns {Object} - Contains the rebalanceCarRatiosMutation function, loading state, error state, and setDisableToast function.
 */
export default function useRebalanceCarRatiosMutationHook(options: RebalanceCarRatiosOptions = {}): {
  rebalanceCarRatiosMutation: ReturnType<typeof useMutation<RebalanceCarRatiosMutation, RebalanceCarRatiosMutationVariables>>[0];
  loading: boolean;
  error: ReturnType<typeof useMutation<RebalanceCarRatiosMutation, RebalanceCarRatiosMutationVariables>>[1]["error"];
  setDisableToast: (_value: boolean) => void;
  } {
  const { disableToast: initialDisableToast = false } = options;
  const disableToastRef = useRef(initialDisableToast);
  const setDisableToast = (value: boolean) => {
    disableToastRef.current = value;
  };

  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [rebalanceCarRatiosMutation, { loading, error }] = useMutation<
    RebalanceCarRatiosMutation,
    RebalanceCarRatiosMutationVariables
  >(RebalanceCarRatiosDocument, {
    onCompleted: () => {
      if (!disableToastRef.current) {
        toast(CustomToastComponent, {
          toastId: "car-ratios-updated-success",
          data: {
            title: t("toasts.carratiosupdated"),
            content: t("toasts.carratiosupdatedsuccessfully"),
            type: "success",
          },
        });
      }

      // Refetch only active FetchServicesOnTender queries for this tender
      // This ensures we make only ONE network call instead of multiple
      // for different parameter combinations
      client.refetchQueries({
        include: [FetchServicesOnTenderDocument],
        onQueryUpdated(observableQuery) {
          // Only refetch queries for this specific tender
          const variables = observableQuery.options.variables as { tenderId?: number } | undefined;
          return variables?.tenderId === Number(tenderID);
        },
      });
    },
    onError: () => {
      toast(CustomToastComponent, {
        toastId: "car-ratios-updated-error",
        data: {
          title: t("toasts.error"),
          content: t("toasts.carratiosupdatederror"),
          type: "error",
        },
      });
    },
    refetchQueries: [
      {
        query: FetchTenderByIdDocument,
        variables: { id: Number(tenderID) },
      },
      {
        query: FetchCarsOnTenderDocument,
        variables: { tenderId: Number(tenderID) },
      },
    ],
  });

  return {
    rebalanceCarRatiosMutation,
    loading,
    error,
    setDisableToast,
  };
}
